import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/* ZIVOL P8.2: the bubble counts the sets, not the free-gift lines under them (Liquid twin: snippets/zvs-bag-count.liquid, which reads the zv-gift tag; cart JSON
   carries only product_type, which is "Free gift" on the three gift products). Every cart event carries the full item list in detail.items, so the number is right
   the moment the event settles and never flashes the gift-inclusive total. */
const GIFT_TYPE = 'Free gift';
const countSets = (items) => items.reduce((n, item) => (item.product_type === GIFT_TYPE ? n : n + item.quantity), 0);

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** FIX-1: counts the cart events seen, so a count read from the cart before an event is never drawn over the count that event brought. @type {number} */
  #epoch = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    this.#epoch++;
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = Array.isArray(detail?.items) ? countSets(detail.items) : (cart?.totalQuantity ?? detail?.itemCount ?? 0);

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    // C6: storage can throw in an in-app webview; the cart count hint is optional, so a failure must not stop the rest
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* no hint kept */
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * FIX-1: sets the bubble to the sets in the cart as it is now (the same rule as the events and as snippets/zvs-bag-count.liquid). A cart event that arrives meanwhile
   * wins, and a failed request leaves what the bubble shows.
   */
  #reconcileWithCart = async () => {
    const epoch = this.#epoch;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (epoch !== this.#epoch || !Array.isArray(cart?.items)) return;
      this.renderCartBubble(countSets(cart.items), false);
    } catch (_) {
      // no-op: the bubble keeps what it shows
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    /** @type {string | null} */
    let sessionStorageCount = null;
    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (_) {
      return; // C6: storage blocked: no hint to check
    }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }

        // FIX-1: the count kept in sessionStorage is a hint for the 10 seconds after a cart event, and it is only as true as the last event: a cart changed without one
        // (another tab, a script that posts to /cart directly) left the hint stale, and a page loaded within 10 s then drew the stale number over the server's right one
        // ("Bag (2)" beside a bubble reading 1). The cart is asked and the bubble set to what it holds; the hint still shows first, so a cached page is still corrected at once.
        this.#reconcileWithCart();
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
